import React, { useState } from 'react';
import { 
  Copy, CheckCircle, Zap, Type, Hash, Link2, AtSign, 
  TrendingUp, Eye, Sparkles, RefreshCw, AlignLeft,
  Scissors, FileText, MessageSquare
} from 'lucide-react';

const RookieToolkit = () => {
  const [activeTab, setActiveTab] = useState('spacezapper');
  const [copied, setCopied] = useState(false);

  // SpaceZapper Tool
  const [spaceZapperInput, setSpaceZapperInput] = useState('');
  const [spaceZapperOutput, setSpaceZapperOutput] = useState('');

  const handleSpaceZapper = () => {
    const cleaned = spaceZapperInput
      .replace(/\s+/g, ' ')  // Replace multiple spaces with single space
      .trim();  // Remove leading/trailing spaces
    setSpaceZapperOutput(cleaned);
  };

  // Case Converter Tool
  const [caseInput, setCaseInput] = useState('');
  const [caseOutput, setCaseOutput] = useState('');

  const handleCaseConvert = (type) => {
    let result = '';
    switch(type) {
      case 'lower':
        result = caseInput.toLowerCase();
        break;
      case 'upper':
        result = caseInput.toUpperCase();
        break;
      case 'title':
        result = caseInput.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
        break;
      case 'sentence':
        result = caseInput.toLowerCase().replace(/(^\s*\w|[.!?]\s*\w)/g, c => c.toUpperCase());
        break;
      default:
        result = caseInput;
    }
    setCaseOutput(result);
  };

  // Line Break Remover
  const [lineBreakInput, setLineBreakInput] = useState('');
  const [lineBreakOutput, setLineBreakOutput] = useState('');

  const handleLineBreakRemoval = () => {
    const cleaned = lineBreakInput
      .replace(/\n+/g, ' ')  // Replace line breaks with space
      .replace(/\s+/g, ' ')  // Remove extra spaces
      .trim();
    setLineBreakOutput(cleaned);
  };

  // Word Counter
  const [counterInput, setCounterInput] = useState('');
  const [counterStats, setCounterStats] = useState({
    characters: 0,
    charactersNoSpaces: 0,
    words: 0,
    sentences: 0,
    paragraphs: 0,
    readingTime: 0
  });

  const handleCount = (text) => {
    setCounterInput(text);
    
    const chars = text.length;
    const charsNoSpaces = text.replace(/\s/g, '').length;
    const words = text.trim().split(/\s+/).filter(w => w.length > 0).length;
    const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0).length;
    const paragraphs = text.split(/\n\n+/).filter(p => p.trim().length > 0).length;
    const readingTime = Math.ceil(words / 200); // 200 words per minute average
    
    setCounterStats({
      characters: chars,
      charactersNoSpaces: charsNoSpaces,
      words: words,
      sentences: sentences,
      paragraphs: paragraphs,
      readingTime: readingTime
    });
  };

  // HTML Tag Stripper
  const [htmlInput, setHtmlInput] = useState('');
  const [htmlOutput, setHtmlOutput] = useState('');

  const handleHtmlStrip = () => {
    const stripped = htmlInput.replace(/<[^>]*>/g, '').trim();
    setHtmlOutput(stripped);
  };

  // Find and Replace
  const [findReplaceInput, setFindReplaceInput] = useState('');
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [findReplaceOutput, setFindReplaceOutput] = useState('');

  const handleFindReplace = () => {
    if (!findText) {
      setFindReplaceOutput(findReplaceInput);
      return;
    }
    const regex = new RegExp(findText, 'gi');
    const replaced = findReplaceInput.replace(regex, replaceText);
    setFindReplaceOutput(replaced);
  };

  // Emoji Inserter
  const [emojiInput, setEmojiInput] = useState('');
  const emojiCategories = {
    'Popular': ['😊', '❤️', '🔥', '⭐', '👍', '🎉', '💪', '✨', '🚀', '💯'],
    'Business': ['💼', '📊', '💰', '📈', '🎯', '🏆', '💡', '📱', '💻', '🌐'],
    'Marketing': ['📢', '🎁', '🛒', '💳', '🔔', '📧', '📲', '🎨', '📸', '🎬'],
    'Success': ['🥇', '🏅', '👑', '💎', '🌟', '⚡', '🔝', '✅', '🎊', '🙌']
  };

  const insertEmoji = (emoji) => {
    setEmojiInput(emojiInput + emoji);
  };

  // URL Encoder/Decoder
  const [urlInput, setUrlInput] = useState('');
  const [urlOutput, setUrlOutput] = useState('');

  const handleUrlEncode = () => {
    setUrlOutput(encodeURIComponent(urlInput));
  };

  const handleUrlDecode = () => {
    try {
      setUrlOutput(decodeURIComponent(urlInput));
    } catch (e) {
      setUrlOutput('Invalid URL encoding');
    }
  };

  // UTM Builder
  const [utmUrl, setUtmUrl] = useState('');
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [utmOutput, setUtmOutput] = useState('');

  const buildUtm = () => {
    if (!utmUrl) return;
    
    let url = utmUrl;
    const params = [];
    
    if (utmSource) params.push(`utm_source=${encodeURIComponent(utmSource)}`);
    if (utmMedium) params.push(`utm_medium=${encodeURIComponent(utmMedium)}`);
    if (utmCampaign) params.push(`utm_campaign=${encodeURIComponent(utmCampaign)}`);
    
    if (params.length > 0) {
      const separator = url.includes('?') ? '&' : '?';
      url += separator + params.join('&');
    }
    
    setUtmOutput(url);
  };

  // Copy to Clipboard Helper
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const tools = [
    { id: 'spacezapper', name: 'SpaceZapper ⚡', icon: Zap, description: 'Remove extra spaces' },
    { id: 'caseconverter', name: 'Case Converter', icon: Type, description: 'Change text case' },
    { id: 'linebreaker', name: 'Line Break Remover', icon: AlignLeft, description: 'Remove line breaks' },
    { id: 'wordcounter', name: 'Word Counter', icon: Hash, description: 'Count words & characters' },
    { id: 'htmlstripper', name: 'HTML Stripper', icon: FileText, description: 'Remove HTML tags' },
    { id: 'findreplace', name: 'Find & Replace', icon: RefreshCw, description: 'Batch text replacement' },
    { id: 'emoji', name: 'Emoji Inserter', icon: Sparkles, description: 'Add emojis to text' },
    { id: 'urlencoder', name: 'URL Encoder', icon: Link2, description: 'Encode/decode URLs' },
    { id: 'utmbuilder', name: 'UTM Builder', icon: TrendingUp, description: 'Create tracking links' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-2">
            <Scissors className="w-10 h-10" />
            <h1 className="text-4xl font-bold">Rookie Toolkit</h1>
          </div>
          <p className="text-xl text-blue-100">
            Professional text editing tools for marketers - Free for all Boot Camp members!
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Tool Grid Navigation */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          {tools.map((tool) => (
            <button
              key={tool.id}
              onClick={() => setActiveTab(tool.id)}
              className={`p-4 rounded-xl border-2 transition ${
                activeTab === tool.id
                  ? 'border-blue-600 bg-blue-50 shadow-lg'
                  : 'border-gray-200 bg-white hover:border-blue-300'
              }`}
            >
              <tool.icon className={`w-8 h-8 mx-auto mb-2 ${
                activeTab === tool.id ? 'text-blue-600' : 'text-gray-400'
              }`} />
              <p className={`font-semibold text-sm text-center ${
                activeTab === tool.id ? 'text-blue-900' : 'text-gray-700'
              }`}>
                {tool.name}
              </p>
              <p className="text-xs text-gray-500 text-center mt-1">{tool.description}</p>
            </button>
          ))}
        </div>

        {/* Tool Content */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          {/* SpaceZapper */}
          {activeTab === 'spacezapper' && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Zap className="w-8 h-8 text-yellow-500" />
                <div>
                  <h2 className="text-2xl font-bold">SpaceZapper ⚡</h2>
                  <p className="text-gray-600">Remove extra spaces between words instantly</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block font-semibold mb-2">Paste Your Text:</label>
                  <textarea
                    value={spaceZapperInput}
                    onChange={(e) => setSpaceZapperInput(e.target.value)}
                    placeholder="Paste text with extra    spaces    here..."
                    className="w-full h-40 border-2 border-gray-300 rounded-lg p-4 focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <button
                  onClick={handleSpaceZapper}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 w-full flex items-center justify-center gap-2"
                >
                  <Zap className="w-5 h-5" />
                  Zap Those Spaces!
                </button>

                {spaceZapperOutput && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block font-semibold">Cleaned Text:</label>
                      <button
                        onClick={() => copyToClipboard(spaceZapperOutput)}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 flex items-center gap-2"
                      >
                        {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <textarea
                      value={spaceZapperOutput}
                      readOnly
                      className="w-full h-40 border-2 border-green-300 bg-green-50 rounded-lg p-4"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Case Converter */}
          {activeTab === 'caseconverter' && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Type className="w-8 h-8 text-blue-500" />
                <div>
                  <h2 className="text-2xl font-bold">Case Converter</h2>
                  <p className="text-gray-600">Transform text to different cases</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block font-semibold mb-2">Your Text:</label>
                  <textarea
                    value={caseInput}
                    onChange={(e) => setCaseInput(e.target.value)}
                    placeholder="Enter text to convert..."
                    className="w-full h-32 border-2 border-gray-300 rounded-lg p-4 focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <button
                    onClick={() => handleCaseConvert('lower')}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                  >
                    lowercase
                  </button>
                  <button
                    onClick={() => handleCaseConvert('upper')}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                  >
                    UPPERCASE
                  </button>
                  <button
                    onClick={() => handleCaseConvert('title')}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                  >
                    Title Case
                  </button>
                  <button
                    onClick={() => handleCaseConvert('sentence')}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                  >
                    Sentence case
                  </button>
                </div>

                {caseOutput && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block font-semibold">Result:</label>
                      <button
                        onClick={() => copyToClipboard(caseOutput)}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 flex items-center gap-2"
                      >
                        {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <textarea
                      value={caseOutput}
                      readOnly
                      className="w-full h-32 border-2 border-green-300 bg-green-50 rounded-lg p-4"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Line Break Remover */}
          {activeTab === 'linebreaker' && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <AlignLeft className="w-8 h-8 text-purple-500" />
                <div>
                  <h2 className="text-2xl font-bold">Line Break Remover</h2>
                  <p className="text-gray-600">Convert multi-line text to single line</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block font-semibold mb-2">Multi-Line Text:</label>
                  <textarea
                    value={lineBreakInput}
                    onChange={(e) => setLineBreakInput(e.target.value)}
                    placeholder="Paste text with line breaks here..."
                    className="w-full h-40 border-2 border-gray-300 rounded-lg p-4 focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <button
                  onClick={handleLineBreakRemoval}
                  className="bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 w-full"
                >
                  Remove Line Breaks
                </button>

                {lineBreakOutput && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block font-semibold">Single Line Text:</label>
                      <button
                        onClick={() => copyToClipboard(lineBreakOutput)}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 flex items-center gap-2"
                      >
                        {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <textarea
                      value={lineBreakOutput}
                      readOnly
                      className="w-full h-32 border-2 border-green-300 bg-green-50 rounded-lg p-4"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Word Counter */}
          {activeTab === 'wordcounter' && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Hash className="w-8 h-8 text-green-500" />
                <div>
                  <h2 className="text-2xl font-bold">Word Counter</h2>
                  <p className="text-gray-600">Analyze your text statistics</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block font-semibold mb-2">Your Text:</label>
                  <textarea
                    value={counterInput}
                    onChange={(e) => handleCount(e.target.value)}
                    placeholder="Type or paste text to analyze..."
                    className="w-full h-40 border-2 border-gray-300 rounded-lg p-4 focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-blue-600">{counterStats.words}</p>
                    <p className="text-sm text-gray-600">Words</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-green-600">{counterStats.characters}</p>
                    <p className="text-sm text-gray-600">Characters</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-purple-600">{counterStats.charactersNoSpaces}</p>
                    <p className="text-sm text-gray-600">No Spaces</p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-yellow-600">{counterStats.sentences}</p>
                    <p className="text-sm text-gray-600">Sentences</p>
                  </div>
                  <div className="bg-pink-50 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-pink-600">{counterStats.paragraphs}</p>
                    <p className="text-sm text-gray-600">Paragraphs</p>
                  </div>
                  <div className="bg-indigo-50 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-indigo-600">{counterStats.readingTime}</p>
                    <p className="text-sm text-gray-600">Min Read</p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold mb-2">Social Media Limits:</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Twitter/X:</span>
                      <span className={counterStats.characters <= 280 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                        {counterStats.characters}/280
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>SMS:</span>
                      <span className={counterStats.characters <= 160 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                        {counterStats.characters}/160
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Facebook Post:</span>
                      <span className={counterStats.characters <= 63206 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                        {counterStats.characters}/63,206
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* HTML Stripper */}
          {activeTab === 'htmlstripper' && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-8 h-8 text-red-500" />
                <div>
                  <h2 className="text-2xl font-bold">HTML Tag Stripper</h2>
                  <p className="text-gray-600">Remove HTML tags from text</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block font-semibold mb-2">HTML Text:</label>
                  <textarea
                    value={htmlInput}
                    onChange={(e) => setHtmlInput(e.target.value)}
                    placeholder="<p>Paste HTML here...</p>"
                    className="w-full h-40 border-2 border-gray-300 rounded-lg p-4 focus:border-blue-500 focus:outline-none font-mono text-sm"
                  />
                </div>

                <button
                  onClick={handleHtmlStrip}
                  className="bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 w-full"
                >
                  Strip HTML Tags
                </button>

                {htmlOutput && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block font-semibold">Plain Text:</label>
                      <button
                        onClick={() => copyToClipboard(htmlOutput)}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 flex items-center gap-2"
                      >
                        {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <textarea
                      value={htmlOutput}
                      readOnly
                      className="w-full h-40 border-2 border-green-300 bg-green-50 rounded-lg p-4"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Find and Replace */}
          {activeTab === 'findreplace' && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <RefreshCw className="w-8 h-8 text-indigo-500" />
                <div>
                  <h2 className="text-2xl font-bold">Find & Replace</h2>
                  <p className="text-gray-600">Batch replace text throughout your content</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block font-semibold mb-2">Your Text:</label>
                  <textarea
                    value={findReplaceInput}
                    onChange={(e) => setFindReplaceInput(e.target.value)}
                    placeholder="Enter text here..."
                    className="w-full h-32 border-2 border-gray-300 rounded-lg p-4 focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-semibold mb-2">Find:</label>
                    <input
                      type="text"
                      value={findText}
                      onChange={(e) => setFindText(e.target.value)}
                      placeholder="Text to find..."
                      className="w-full border-2 border-gray-300 rounded-lg p-3 focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold mb-2">Replace with:</label>
                    <input
                      type="text"
                      value={replaceText}
                      onChange={(e) => setReplaceText(e.target.value)}
                      placeholder="Replacement text..."
                      className="w-full border-2 border-gray-300 rounded-lg p-3 focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                </div>

                <button
                  onClick={handleFindReplace}
                  className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 w-full"
                >
                  Replace All
                </button>

                {findReplaceOutput && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block font-semibold">Result:</label>
                      <button
                        onClick={() => copyToClipboard(findReplaceOutput)}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 flex items-center gap-2"
                      >
                        {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <textarea
                      value={findReplaceOutput}
                      readOnly
                      className="w-full h-32 border-2 border-green-300 bg-green-50 rounded-lg p-4"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Emoji Inserter */}
          {activeTab === 'emoji' && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="w-8 h-8 text-yellow-500" />
                <div>
                  <h2 className="text-2xl font-bold">Emoji Inserter</h2>
                  <p className="text-gray-600">Add emojis to make your copy pop!</p>
                </div>
              </div>

              <div className="space-y-4">
                {Object.entries(emojiCategories).map(([category, emojis]) => (
                  <div key={category}>
                    <h3 className="font-semibold mb-2">{category}</h3>
                    <div className="flex flex-wrap gap-2">
                      {emojis.map((emoji, idx) => (
                        <button
                          key={idx}
                          onClick={() => insertEmoji(emoji)}
                          className="text-3xl hover:scale-125 transition p-2 hover:bg-gray-100 rounded"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}

                <div>
                  <label className="block font-semibold mb-2">Your Text with Emojis:</label>
                  <textarea
                    value={emojiInput}
                    onChange={(e) => setEmojiInput(e.target.value)}
                    placeholder="Click emojis above to insert them here..."
                    className="w-full h-40 border-2 border-gray-300 rounded-lg p-4 focus:border-blue-500 focus:outline-none"
                  />
                </div>

                {emojiInput && (
                  <button
                    onClick={() => copyToClipboard(emojiInput)}
                    className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 w-full flex items-center justify-center gap-2"
                  >
                    {copied ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                    {copied ? 'Copied!' : 'Copy Text'}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* URL Encoder */}
          {activeTab === 'urlencoder' && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Link2 className="w-8 h-8 text-blue-500" />
                <div>
                  <h2 className="text-2xl font-bold">URL Encoder/Decoder</h2>
                  <p className="text-gray-600">Encode or decode URL strings</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block font-semibold mb-2">URL or Text:</label>
                  <textarea
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    placeholder="Enter URL or text..."
                    className="w-full h-32 border-2 border-gray-300 rounded-lg p-4 focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={handleUrlEncode}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
                  >
                    Encode
                  </button>
                  <button
                    onClick={handleUrlDecode}
                    className="bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700"
                  >
                    Decode
                  </button>
                </div>

                {urlOutput && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block font-semibold">Result:</label>
                      <button
                        onClick={() => copyToClipboard(urlOutput)}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 flex items-center gap-2"
                      >
                        {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <textarea
                      value={urlOutput}
                      readOnly
                      className="w-full h-32 border-2 border-green-300 bg-green-50 rounded-lg p-4 font-mono text-sm"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* UTM Builder */}
          {activeTab === 'utmbuilder' && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <TrendingUp className="w-8 h-8 text-green-500" />
                <div>
                  <h2 className="text-2xl font-bold">UTM Parameter Builder</h2>
                  <p className="text-gray-600">Create trackable campaign links</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block font-semibold mb-2">Website URL *</label>
                  <input
                    type="text"
                    value={utmUrl}
                    onChange={(e) => setUtmUrl(e.target.value)}
                    placeholder="https://example.com"
                    className="w-full border-2 border-gray-300 rounded-lg p-3 focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block font-semibold mb-2">Campaign Source</label>
                  <input
                    type="text"
                    value={utmSource}
                    onChange={(e) => setUtmSource(e.target.value)}
                    placeholder="facebook, google, newsletter"
                    className="w-full border-2 border-gray-300 rounded-lg p-3 focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block font-semibold mb-2">Campaign Medium</label>
                  <input
                    type="text"
                    value={utmMedium}
                    onChange={(e) => setUtmMedium(e.target.value)}
                    placeholder="cpc, banner, email"
                    className="w-full border-2 border-gray-300 rounded-lg p-3 focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block font-semibold mb-2">Campaign Name</label>
                  <input
                    type="text"
                    value={utmCampaign}
                    onChange={(e) => setUtmCampaign(e.target.value)}
                    placeholder="summer_sale, product_launch"
                    className="w-full border-2 border-gray-300 rounded-lg p-3 focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <button
                  onClick={buildUtm}
                  className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 w-full"
                >
                  Build Tracking URL
                </button>

                {utmOutput && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block font-semibold">Your Tracking URL:</label>
                      <button
                        onClick={() => copyToClipboard(utmOutput)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 flex items-center gap-2"
                      >
                        {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        {copied ? 'Copied!' : 'Copy URL'}
                      </button>
                    </div>
                    <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 break-all font-mono text-sm">
                      {utmOutput}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Pro Tips Section */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mt-8">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Eye className="w-6 h-6 text-blue-600" />
            Pro Tips for Using the Rookie Toolkit
          </h3>
          <ul className="space-y-2 text-gray-700">
            <li>• <strong>SpaceZapper:</strong> Perfect for cleaning text copied from PDFs or websites</li>
            <li>• <strong>Case Converter:</strong> Use Title Case for headlines, Sentence case for body copy</li>
            <li>• <strong>Word Counter:</strong> Keep emails under 200 words for best engagement</li>
            <li>• <strong>Emoji Inserter:</strong> Use 1-2 emojis per post for mobile-first audiences</li>
            <li>• <strong>UTM Builder:</strong> Track which marketing channels drive the most leads</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default RookieToolkit;
